import type { CSSProperties, FC } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box } from '@mui/material'
import { Highlight, Prism, themes } from 'prism-react-renderer'
import { CopyButton } from '@/common/components'

globalThis.Prism = Prism
// @ts-expect-error - prismjs components don't have type definitions
await import('prismjs/components/prism-bash')

interface CodeBlockProps {
  readonly code: string
  readonly language?: string
}

export const CommandBlock: FC<CodeBlockProps> = ({ code, language = 'bash' }) => (
  <Box sx={codeContainerSx}>
    <Box className="code-copy-button" sx={copyButtonWrapperSx}>
      <CopyButton text={code} variant="overlay" tooltipText="Copy" />
    </Box>
    <Highlight theme={themes.vsDark} code={code} language={language} prism={Prism}>
      {({ style, tokens, getLineProps, getTokenProps }) => (
        <Box component="pre" sx={codeBlockSx(style)}>
          {tokens.map((line, lineIndex) => {
            const lineProps = getLineProps({ line })
            const lineContent = line.map((t) => t.content).join('')
            return (
              <div key={`${lineIndex}-${lineContent.slice(0, 20)}`} {...lineProps}>
                {line.map((token, tokenIndex) => (
                  <span
                    key={`${lineIndex}-${tokenIndex}-${token.content.slice(0, 10)}`}
                    {...getTokenProps({ token })}
                  />
                ))}
              </div>
            )
          })}
        </Box>
      )}
    </Highlight>
  </Box>
)

// Styles

const codeContainerSx: SxProps<Theme> = {
  position: 'relative',
  my: 1.5,
  '&:hover .code-copy-button, &:focus-within .code-copy-button': {
    opacity: 1,
  },
}

const copyButtonWrapperSx: SxProps<Theme> = {
  position: 'absolute',
  top: 8,
  right: 8,
  zIndex: 1,
  opacity: 0,
  transition: 'opacity 0.2s ease-in-out',
}

const codeBlockSx = (highlightStyle: CSSProperties): SxProps<Theme> => ({
  ...(highlightStyle as Record<string, unknown>),
  p: 2,
  m: 0,
  fontFamily: 'monospace',
  fontSize: '0.875rem',
  borderRadius: 1,
  overflow: 'auto',
  whiteSpace: 'pre',
  lineHeight: 1.6,
})
